import {
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    serverTimestamp,
    setDoc,
} from 'firebase/firestore';
import { db } from '@/config/firebase';

export const EVALUATION_THRESHOLD_COLLECTION = 'performance_evaluation_thresholds';

export const EVALUATION_AXIS_KEYS = ['AGIR', 'CUIDAR', 'INOVAR', 'INFLUENCIAR'] as const;

export type EvaluationAxisKey = typeof EVALUATION_AXIS_KEYS[number];

export type EvaluationAxisThresholds = Partial<Record<EvaluationAxisKey | string, number>>;

export interface EvaluationThresholdConfig {
    id: string;
    positionId: string;
    positionName: string;
    seniorityId: string;
    seniorityName: string;
    thresholds: EvaluationAxisThresholds;
    createdAt?: unknown;
    updatedAt?: unknown;
}

export interface SaveEvaluationThresholdConfigInput {
    positionId: string;
    positionName: string;
    seniorityId: string;
    seniorityName: string;
    thresholds: EvaluationAxisThresholds;
}

export const EVALUATION_AXIS_LABELS: Record<EvaluationAxisKey, string> = {
    AGIR: 'AGIR',
    CUIDAR: 'CUIDAR',
    INOVAR: 'INOVAR',
    INFLUENCIAR: 'INFLUENCIAR',
};

function normalizeIdPart(value: string): string {
    return value.trim().replace(/\//g, '_');
}

export function buildEvaluationThresholdId(positionId: string, seniorityId: string): string {
    return `${normalizeIdPart(positionId)}__${normalizeIdPart(seniorityId)}`;
}

function normalizeThresholdValue(value: unknown): number | null {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return null;
    }

    if (value < 0 || value > 1) {
        return null;
    }

    return value;
}

function normalizeThresholds(thresholds: EvaluationAxisThresholds): EvaluationAxisThresholds {
    return EVALUATION_AXIS_KEYS.reduce<EvaluationAxisThresholds>((acc, axis) => {
        const value = normalizeThresholdValue(thresholds[axis]);
        if (value !== null) {
            acc[axis] = value;
        }
        return acc;
    }, {});
}

function mapEvaluationThresholdConfig(id: string, data: Record<string, unknown>): EvaluationThresholdConfig {
    return {
        id,
        positionId: typeof data.positionId === 'string' ? data.positionId : '',
        positionName: typeof data.positionName === 'string' ? data.positionName : '',
        seniorityId: typeof data.seniorityId === 'string' ? data.seniorityId : '',
        seniorityName: typeof data.seniorityName === 'string' ? data.seniorityName : '',
        thresholds: normalizeThresholds((data.thresholds ?? {}) as EvaluationAxisThresholds),
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
    };
}

export async function listEvaluationThresholdConfigs(): Promise<EvaluationThresholdConfig[]> {
    const snapshot = await getDocs(collection(db, EVALUATION_THRESHOLD_COLLECTION));

    return snapshot.docs
        .map((configDoc) => mapEvaluationThresholdConfig(configDoc.id, configDoc.data()))
        .sort((left, right) => (
            left.positionName.localeCompare(right.positionName, 'pt-BR', { sensitivity: 'base' })
            || left.seniorityName.localeCompare(right.seniorityName, 'pt-BR', { sensitivity: 'base' })
        ));
}

export async function getEvaluationThresholdConfig(
    positionId?: string | null,
    seniorityId?: string | null
): Promise<EvaluationThresholdConfig | null> {
    if (!positionId || !seniorityId) {
        return null;
    }

    const configDoc = await getDoc(doc(
        db,
        EVALUATION_THRESHOLD_COLLECTION,
        buildEvaluationThresholdId(positionId, seniorityId)
    ));

    if (!configDoc.exists()) {
        return null;
    }

    return mapEvaluationThresholdConfig(configDoc.id, configDoc.data());
}

export async function saveEvaluationThresholdConfig(input: SaveEvaluationThresholdConfigInput): Promise<void> {
    const id = buildEvaluationThresholdId(input.positionId, input.seniorityId);
    const ref = doc(db, EVALUATION_THRESHOLD_COLLECTION, id);
    const existing = await getDoc(ref);

    await setDoc(ref, {
        positionId: input.positionId,
        positionName: input.positionName,
        seniorityId: input.seniorityId,
        seniorityName: input.seniorityName,
        thresholds: normalizeThresholds(input.thresholds),
        createdAt: existing.exists() ? existing.data().createdAt ?? serverTimestamp() : serverTimestamp(),
        updatedAt: serverTimestamp(),
    }, { merge: true });
}

export async function deleteEvaluationThresholdConfig(id: string): Promise<void> {
    await deleteDoc(doc(db, EVALUATION_THRESHOLD_COLLECTION, id));
}
